import { Check, ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2, X } from "lucide-react"
import * as React from "react"

import { SortHeader, type SortState } from "@/components/rules/sort-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ApiError,
  deleteListEntry,
  listEntries,
  listFieldTypes,
  LIST_PAGE_SIZE,
  updateListEntry,
  type FieldTypes,
  type ListEntriesPage,
  type ListEntry,
  type SortOrder,
} from "@/lib/api"
import { EMPTY, formatDate, formatInt, formatText, formatTime } from "@/lib/format"
import { AddValuePanel } from "@/pages/lists/add-value"

/** Шесть колонок §4 плюс седьмая — правка и удаление строки. */
const COLUMNS = 7

/** Имя списка этой вкладки. То же, что читает проверка членства в скоринге. */
const LIST = "blacklist"

/** Значение фильтра «все типы». Не пустая строка: `Select` её не принимает. */
const ANY_TYPE = "__any__"

/**
 * Сортировка §4 — по дате правки, свежие сверху. Ключ один: ручка сортирует
 * только по нему, и стрелка над колонкой, которую сервер переставлять не умеет,
 * была бы хуже её отсутствия.
 */
const DEFAULT_SORT: SortState = { sort: "updated_at", order: "desc" }

/**
 * Lists → Blacklist (`/lists`, AMEND-12 §4).
 *
 * Всё, что видно, приходит из `/api/lists/blacklist`: строки, счётчик, порядок,
 * страница и набор типов в фильтре. На клиенте не досчитывается ничего — иначе
 * «всего N» и содержимое таблицы могли бы разъехаться незаметно.
 *
 * Значения списка — персональные данные живых людей. Отсюда страница в 25 строк
 * вместо сотни у транзакций и отсутствие выгрузки в файл: чем меньше ПДн
 * одновременно на экране, тем меньше их в случайном скриншоте.
 *
 * Истёкшая запись **не исчезает** — членством она не считается (§5), но в
 * списке видна с пометкой `expired`: пропади она, человек решил бы, что запись
 * кто-то удалил, и завёл бы её заново.
 */
export function BlacklistTab() {
  const [search, setSearch] = React.useState("")
  const [query, setQuery] = React.useState("")
  const [fieldType, setFieldType] = React.useState(ANY_TYPE)
  const [sortState, setSortState] = React.useState<SortState>(DEFAULT_SORT)
  const [offset, setOffset] = React.useState(0)
  const [reload, setReload] = React.useState(0)
  const [adding, setAdding] = React.useState(false)

  const [data, setData] = React.useState<ListEntriesPage | null>(null)
  const [types, setTypes] = React.useState<FieldTypes | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // Задержка ввода: без неё каждая буква уходит отдельным запросом, а поиск
  // здесь идёт по 30 000 записей.
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim())
      setOffset(0)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search])

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    listEntries(
      LIST,
      {
        q: query || undefined,
        field_type: fieldType === ANY_TYPE ? undefined : fieldType,
        order: sortState.order as SortOrder,
        limit: LIST_PAGE_SIZE,
        offset,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [query, fieldType, sortState, offset, reload])

  // Набор типов живёт своим запросом и обновляется вместе с таблицей: заведи
  // человек девятый тип — он обязан появиться в фильтре, не дожидаясь
  // перезагрузки страницы. В зависимостях нет ни поиска, ни страницы: счётчики
  // считаются по всему списку, а не по видимому срезу.
  React.useEffect(() => {
    const controller = new AbortController()
    listFieldTypes(LIST, controller.signal)
      .then(setTypes)
      .catch(() => {
        // Молча: фильтр — удобство, и его отказ не повод накрывать ошибкой
        // таблицу, которая загрузилась. Ошибку таблицы видно отдельно.
      })
    return () => controller.abort()
  }, [reload])

  const refresh = React.useCallback(() => setReload((value) => value + 1), [])

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + LIST_PAGE_SIZE, total)
  const known = types?.known ?? []

  return (
    <div className="p-5">
      <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle"
              strokeWidth={2}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search value"
              aria-label="Search blacklist by value"
              className="w-64 pl-7"
            />
          </div>

          <Select
            value={fieldType}
            onValueChange={(next) => {
              setFieldType(next)
              setOffset(0)
            }}
          >
            <SelectTrigger className="w-52" aria-label="Data field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_TYPE}>All data fields</SelectItem>
              {/* Только те типы, что в списке **есть**: пункт, дающий ноль
                  строк, читался бы как сломанный фильтр. Восьмёрка известных
                  имён живёт в форме заведения, ей место там. */}
              {(types?.present ?? []).map((item) => (
                <SelectItem key={item.field_type} value={item.field_type}>
                  {item.field_type} ({formatInt(item.count)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-xs text-fg-muted">
            {loading && !data ? EMPTY : `${formatInt(total)} entries`}
          </span>

          <Button
            variant="primary"
            className="ml-auto"
            onClick={() => setAdding((value) => !value)}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
            Add value
          </Button>
        </div>

        {adding && (
          <AddValuePanel
            list={LIST}
            knownTypes={known}
            onClose={() => setAdding(false)}
            onAdded={() => {
              setAdding(false)
              // Свежая запись — самая свежая по дате правки, поэтому на первую
              // страницу порядка по умолчанию. Дописать её в текущий срез руками
              // значило бы показать строку не на её месте.
              setOffset(0)
              setSortState(DEFAULT_SORT)
              refresh()
            }}
          />
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {/* Нижняя граница ширины, а не только желаемая: на 390px
                    браузер иначе ужимает колонку значения до пяти символов в
                    строку, и хеш карты растягивает ряд на семь строк текста.
                    Таблица и так уезжает горизонтальным скроллом — пусть
                    уезжает она, а не читаемость значения. */}
                <TableHead className="w-72 min-w-[15rem]">Value</TableHead>
                <TableHead className="w-40 min-w-[9rem]">Data field</TableHead>
                <TableHead className="w-56 min-w-[11rem]">Comment</TableHead>
                <TableHead className="w-36 min-w-[7rem]">Expiration</TableHead>
                <TableHead className="w-48 min-w-[11rem]">Last updated by</TableHead>
                <SortHeader
                  column="updated_at"
                  state={sortState}
                  onSort={(next) => {
                    setSortState(next)
                    setOffset(0)
                  }}
                  className="w-32"
                >
                  Last updated
                </SortHeader>
                <TableHead className="w-28" align="right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading && items.length === 0 && <LoadingRows />}

              {!loading && error && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMNS} className="px-4 py-10 text-center">
                    <p className="text-sm font-medium text-decline">{error}</p>
                    <p className="mt-1 text-xs text-fg-muted">
                      The Admin API did not answer. The table stays empty on purpose — it is not
                      showing stale entries.
                    </p>
                    <Button className="mt-4" onClick={refresh}>
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {!loading && !error && items.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMNS} className="px-4 py-10 text-center">
                    <p className="text-sm text-fg">
                      {query || fieldType !== ANY_TYPE
                        ? "No entries match this filter."
                        : "The blacklist is empty."}
                    </p>
                    <p className="mt-1 text-xs text-fg-muted">
                      {query || fieldType !== ANY_TYPE
                        ? "The search looks at the value itself — not at comments or authors."
                        : "Use Add value to put the first entry in."}
                    </p>
                  </TableCell>
                </TableRow>
              )}

              {items.map((entry) => (
                <EntryRow key={entry.id} entry={entry} onChanged={refresh} />
              ))}
            </TableBody>
          </Table>
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="text-xs text-fg-muted">
            {formatInt(from)} - {formatInt(to)} of {formatInt(total)}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={offset === 0 || loading}
              onClick={() => setOffset((value) => Math.max(0, value - LIST_PAGE_SIZE))}
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
              Prev
            </Button>
            <Button
              size="sm"
              disabled={!data?.has_more || loading}
              onClick={() => setOffset((value) => value + LIST_PAGE_SIZE)}
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Button>
          </div>
        </footer>
      </section>
    </div>
  )
}

// ── строки ───────────────────────────────────────────────────────────────────

/**
 * Строка списка: показ, правка комментария со сроком, удаление.
 *
 * Правка идёт **в самой строке**, а не в отдельном окне: менять здесь можно
 * ровно два поля из шести (§4), и окно ради двух полей заставляло бы человека
 * терять из виду ту запись, которую он правит.
 */
function EntryRow({ entry, onChanged }: { entry: ListEntry; onChanged: () => void }) {
  const [editing, setEditing] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const [comment, setComment] = React.useState(entry.comment ?? "")
  const [expires, setExpires] = React.useState(entry.expires_at?.slice(0, 10) ?? "")

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await updateListEntry(LIST, entry.id, {
        comment: comment.trim() ? comment.trim() : null,
        // Пустая дата — снять срок, а не «оставить как было»: ручка отличает
        // присланный `null` от неприсланного ключа.
        expires_at: expires ? `${expires}T23:59:59Z` : null,
      })
      setEditing(false)
      onChanged()
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the entry.")
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    setError(null)
    try {
      await deleteListEntry(LIST, entry.id)
      onChanged()
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete the entry.")
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <TableRow className="hover:bg-transparent">
      {/* Значение не переносится по буквам и не обрезается многоточием: хеш
          карты, обрезанный на середине, не отличим от другого такого же. */}
      <TableCell className="break-all text-fg">{entry.value}</TableCell>
      <TableCell className="text-fg-muted">{formatText(entry.field_type)}</TableCell>

      <TableCell className="text-fg-muted">
        {editing ? (
          <Input
            value={comment}
            disabled={busy}
            aria-label="Comment"
            onChange={(event) => setComment(event.target.value)}
            placeholder="Optional"
          />
        ) : (
          formatText(entry.comment)
        )}
        {error && (
          <Notice tone="error" className="mt-1">
            {error}
          </Notice>
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap">
        {editing ? (
          <Input
            type="date"
            value={expires}
            disabled={busy}
            aria-label="Expiration"
            onChange={(event) => setExpires(event.target.value)}
          />
        ) : (
          <ExpirationCell entry={entry} />
        )}
      </TableCell>

      <TableCell className="break-all text-fg-muted">{formatText(entry.updated_by)}</TableCell>

      <TableCell className="whitespace-nowrap text-xs">
        <span className="block text-fg">{formatDate(entry.updated_at)}</span>
        <span className="block text-fg-subtle">{formatTime(entry.updated_at)}</span>
      </TableCell>

      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {editing ? (
            <>
              <Button size="sm" variant="primary" disabled={busy} onClick={save}>
                <Check className="h-3.5 w-3.5" strokeWidth={2.25} />
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                aria-label="Cancel editing"
                onClick={() => {
                  setEditing(false)
                  setError(null)
                  setComment(entry.comment ?? "")
                  setExpires(entry.expires_at?.slice(0, 10) ?? "")
                }}
              >
                <X className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Button>
            </>
          ) : confirming ? (
            // Удаление в два хода: подтверждение стоит на месте самой кнопки,
            // и промахнуться по строке между ходами нельзя — соседние записи
            // отличаются одним символом значения.
            <>
              <Button size="sm" variant="danger" disabled={busy} onClick={remove}>
                {busy ? "Deleting…" : "Delete"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                aria-label="Keep the entry"
                onClick={() => setConfirming(false)}
              >
                <X className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Edit comment and expiration"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Delete entry"
                onClick={() => setConfirming(true)}
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

/**
 * Срок: дата, а у истёкшей — ещё и пометка.
 *
 * Пометку ставит сервер (`expired`), а не сравнение в браузере: у клиента свои
 * часы, и на отставшем компьютере просроченная запись выглядела бы живой.
 */
function ExpirationCell({ entry }: { entry: ListEntry }) {
  if (!entry.expires_at) {
    return <span className="text-fg-subtle">Indefinite</span>
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={entry.expired ? "text-fg-subtle line-through" : "text-fg"}>
        {formatDate(entry.expires_at)}
      </span>
      {entry.expired && (
        <span className="rounded-sm border border-line bg-surface-sunken px-1.5 py-px text-xs uppercase leading-4 tracking-[0.04em] text-fg-muted">
          expired
        </span>
      )}
    </span>
  )
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 8 }, (_, row) => (
        <TableRow key={row} className="hover:bg-transparent">
          {Array.from({ length: COLUMNS }, (_, column) => (
            <TableCell key={column}>
              <Skeleton className="h-3 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}
